import { createPortal } from 'react-dom'

import type { Reaction } from '../../api/types'
import { XIcon } from '../../components/Icons'
import { useEscape } from '../../hooks/useEscape'
import { useTexte } from '../../texte'

/**
 * Who reacted, emoji by emoji (#110).
 *
 * From use: "wie kann man als Autor sehen, wer die Emojis zu allgemeinen
 * Beiträgen gesendet hat." The names were always there -- the server has sent
 * `who` to the author since the reactions exist -- but they stood only in a
 * `title` and a visually hidden span. A tooltip does not exist on a phone.
 *
 * **This file decides nothing about who may see a name.**
 * `Visibility::reactions()` does: a reader gets only their own reaction and
 * those on their own message. What arrives here is shown; what does not
 * arrive cannot be shown -- the same build as `Abstimmungsblatt`.
 *
 * **In the body and in the house form** (`createPortal`, `.bogen`, Escape),
 * for the reason in `CLAUDE.md`: a sheet inside the timeline would stick to
 * it through `backdrop-filter`.
 */
export interface Reaktionsgruppe {
  /** Missing when there is only one target -- a heading over one list says nothing. */
  titel?: string
  reactions: Reaction[]
}

/**
 * Whether the list would show more than one's own name.
 *
 * **Derived from what the server sent, not from a role.** A second rule
 * here ("only the author") would be the same rule in two places; the server
 * already left out every name the viewer may not see.
 */
export function zeigtFremdeNamen(reactions: Reaction[]): boolean {
  return reactions.some((r) => r.who.length > (r.mine ? 1 : 0))
}

export function Reaktionsliste({ gruppen, onZu }: { gruppen: Reaktionsgruppe[]; onZu: () => void }) {
  const t = useTexte()
  useEscape(onZu)
  const belegt = gruppen.filter((g) => g.reactions.length > 0)

  return createPortal(
    <div
      className="bogen bogen--reaktionen"
      role="dialog"
      aria-modal="true"
      aria-label={t.chat.reaktionenTitel}
      data-reaktionsliste
      // A layer on top keeps its gestures (CLAUDE.md): a portal still
      // bubbles through the React tree, and the long press of the bubble
      // underneath would open its menu again.
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="bogen__blatt bogen__blatt--gegliedert">
        <div className="bogen__kopf">
          <h3 className="bogen__titel">{t.chat.reaktionenTitel}</h3>
          <button type="button" className="icon-button" onClick={onZu} aria-label={t.allgemein.schliessen}>
            <XIcon size={20} />
          </button>
        </div>

        <div className="bogen__rolle">
          {belegt.map((gruppe, index) => (
            <section key={gruppe.titel ?? index}>
              {gruppe.titel !== undefined && belegt.length > 1 && (
                <h4 className="ergebnis__ueberschrift">{gruppe.titel}</h4>
              )}
              <ul className="ergebnis__leute">
                {gruppe.reactions.map((reaction) => (
                  <li key={reaction.emoji} data-reaktion={reaction.emoji}>
                    <span className="reaktionsliste__emoji">{reaction.emoji}</span>
                    <span className="reaktionsliste__namen" data-reaktion-namen>
                      {reaction.who.join(', ')}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  )
}
